import React, { useState, useCallback, useEffect } from 'react';
import '../css/App.css';
import {
  FaBars,
  FaUserAlt,
  FaUserTie,
  FaCog,
  FaEye,
  FaTasks,
  FaRunning,
  FaEdit,
  FaPeopleArrows,
  FaFileImport,
  FaBan,
} from "react-icons/fa";
import { SiCashapp, SiReadthedocs } from "react-icons/si";
import { BiLineChartDown } from "react-icons/bi";
import { FiPower } from "react-icons/fi";
import { NavLink } from 'react-router-dom';
import { RiSurveyFill } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from '../store/authStore';
import { logoutApi } from '../security/AuthService';
import { Button, Tooltip, Box } from '@mui/material';
import Copyright from './Copyright';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { logActionType } from "../services/MyCollections";
import { logActivity } from '../services/ApiServices';
import * as CommonFunctions from '../services/CommonFunctions';
import FinancialPopup from './FinancialPopup';
import logo from '../img/logo-top-6f2b56d66f.png';
import logo1 from '../img/logo-top-white.png';
import { useThemeContext } from './contexts/ThemeContext';
import { Outlet } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { pathNameMap, getPathTitleForRoles } from '../services/MyCollections';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import api from '../security/AxiosConfig';
import Notifications from './Notifications';
import TwoFactorBanner from './TwoFactorBanner';
import EnforcedTwoFactorModal from './EnforcedTwoFactorModal';

/* Helpers */
const getInitials = (fullName) => {
  if (!fullName) return '?';
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const hasRole = (roles, targetRole) => {
  if (!roles) return false;
  const list = Array.isArray(roles)
    ? roles
    : (typeof roles === 'string' && roles.includes(',') ? roles.split(',') : [roles]);
  const target = targetRole.trim().toUpperCase();
  const targetWithRole = target.startsWith('ROLE_') ? target : `ROLE_${target}`;
  const targetWithoutRole = target.startsWith('ROLE_') ? target.substring(5) : target;

  return list.some(r => {
    if (!r) return false;
    if (typeof r === 'string') {
      const clean = r.trim().toUpperCase();
      return clean === targetWithoutRole || clean === targetWithRole;
    }
    if (typeof r === 'object') {
      const auth = ((r.authority || r.role || r.name || '') + '').trim().toUpperCase();
      return auth === targetWithoutRole || auth === targetWithRole;
    }
    return false;
  });
};

const Sidebar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const toggle = useCallback(() => setIsOpen(prev => !prev), []);

  const signOut = useAuthStore(state => state.logout);
  const navigate = useNavigate();
  const authData = useAuthStore();
  const role = authData?.roles || [];
  const token = authData?.token;
  const contactId = authData?.contactId;
  const rolesString = Array.isArray(role)
    ? role.map(r => (typeof r === 'object' ? (r.authority || r.role || '') : r)).join(',')
    : String(role || '');
  const userName = authData?.fullName;
  const initials = getInitials(userName);

  const getEffectiveRoles = useCallback(() => {
    const storeRoles = useAuthStore.getState().roles;
    if (Array.isArray(storeRoles) && storeRoles.length > 0) return storeRoles;
    if (Array.isArray(role) && role.length > 0) return role;
    if (Array.isArray(authData?.roles) && authData.roles.length > 0) return authData.roles;
    if (storeRoles) return [storeRoles];
    if (role) return [role];
    if (authData?.roles) return [authData.roles];
    return [];
  }, [role, authData?.roles]);

  const effectiveRoles = getEffectiveRoles();

  const isViewOnly = hasRole(effectiveRoles, 'ACCOUNTANT') || hasRole(effectiveRoles, 'SUPERVISOR_ACCOUNTANT');
  const location = useLocation();
  const currentPath = location.pathname;
  const pageTitle = getPathTitleForRoles(currentPath, effectiveRoles);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));

  const onSignOut = useCallback(async () => {
    try {
      await logActivity(logActionType.LOGOUT, userName, token);
      const refreshToken = useAuthStore.getState().getRefreshToken();
      await logoutApi(token, refreshToken);
    } catch (e) {
      console.error(e);
    }
    signOut();
    sessionStorage.clear();
    localStorage.removeItem('pelikan-auth');
    localStorage.removeItem('pelikan-last-activity');
    navigate("/SignIn");
  }, [signOut, userName, token, navigate]);

  const [isVisible, setIsVisible] = useState(true);
  const { mode, toggleTheme } = useThemeContext();
  const darkTheme = mode === 'dark';

  const toggleVisibility = useCallback(() => {
    setIsVisible(prev => !prev);
  }, []);

  const [notifOpen, setNotifOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);

  // Pobieranie i synchronizacja statusu 2FA
  useEffect(() => {
    if (token) {
      api.get('/api/auth/2fa/status')
        .then(res => {
          if (res.data) {
            useAuthStore.getState().setMfaStatus(res.data);
          }
        })
        .catch(err => console.debug("2FA status fetch:", err));
    }
  }, [token]);

  useEffect(() => {
    const currentRoles = getEffectiveRoles();
    if (currentRoles.length === 0 || hasRole(currentRoles, 'EXTERNAL_IT')) {
      return;
    }

    const rolesToCheck = ['MANAGER', 'SUPERVISOR', 'SUPERVISOR_ACCOUNTANT'];
    const hasAccess = rolesToCheck.some(r => hasRole(currentRoles, r));
    const isAccountant = ['ACCOUNTANT', 'CHIEF_ACCOUNTANT', 'SUPERVISOR_ACCOUNTANT'].some(r => hasRole(currentRoles, r));
    const NOTIFICATION_ROLES = [
      'ACCOUNTANT', 'CHIEF_ACCOUNTANT', 'SUPERVISOR_ACCOUNTANT',
      'SUPERVISOR', 'MANAGER', 'ADMIN', 'ADMIN_SUPERVISOR',
      'ACCOUNT_MANAGER', 'TRADER'
    ];
    const canReceiveNotifications = !hasRole(currentRoles, 'EXTERNAL_IT') && NOTIFICATION_ROLES.some(r => hasRole(currentRoles, r));

    const loadNotifications = async () => {
      let dynamicNotifs = [];
      let dbNotifs = [];

      try {
        if (hasAccess) {
          const res = await api.get('/accountingSettlement/pending-approvals');
          if (res.data && res.data.length > 0) {
            const monthNames = [
              "", "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
              "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"
            ];
            dynamicNotifs = res.data.map(item => ({
              message: `Zestawienie księgowego ${item.accountantName} za miesiąc ${monthNames[item.month]} nie zostało zatwierdzone (upłynęło 8 dni od wysłania)`,
              isPendingApproval: true
            }));
          }
        }
      } catch (err) {
        console.error("Error fetching pending approvals:", err);
      }

      try {
        if (isAccountant && contactId) {
          const res = await api.get(`/accountingSettlement/pending-corrections/${contactId}`);
          if (res.data && res.data.length > 0) {
            const monthNames = [
              "", "styczeń", "luty", "marzec", "kwiecień", "maj", "czerwiec",
              "lipiec", "sierpień", "wrzesień", "październik", "listopad", "grudzień"
            ];
            const correctionNotifs = res.data.map(item => {
              if (item.isBlockCorrection) {
                return {
                  message: `Wymagane wgranie faktury korygującej ze względu na blokadę wynagrodzenia za miesiąc ${monthNames[item.month]} ${item.year}. Szczegóły w zakładce Blokady`,
                  isCorrection: true
                };
              } else {
                return {
                  message: `Wymagane wgranie faktury korygującej za prace dodatkowe za miesiąc ${monthNames[item.month]} ${item.year}. Szczegóły w zakładce Prace dodatkowe`,
                  isCorrection: true
                };
              }
            });
            dynamicNotifs = [...dynamicNotifs, ...correctionNotifs];
          }
        }
      } catch (err) {
        console.error("Error fetching pending corrections:", err);
      }

      // Powiadomienia o korektach faktur do sprawozdań finansowych
      try {
        if (isAccountant && contactId) {
          const res = await api.get(`/accountant-financial-statement-invoices/pending-corrections/${contactId}`);
          if (res.data && res.data.length > 0) {
            const fsCorrectionNotifs = res.data.map(item => ({
              message: `Wymagane wgranie faktury korygującej do sprawozdania finansowego za rok ${item.year}. Szczegóły w zakładce Sprawozdania Finansowe`,
              isCorrection: true
            }));
            dynamicNotifs = [...dynamicNotifs, ...fsCorrectionNotifs];
          }
        }
      } catch (err) {
        console.error("Error fetching financial statement pending corrections:", err);
      }

      try {
        if (contactId && canReceiveNotifications) {
          const res = await api.get(`/notification/${contactId}`);
          if (res.data && res.data.length > 0) {
            dbNotifs = res.data;
          }
        }
      } catch (err) {
        console.error("Error fetching db notifications:", err);
      }

      const combined = [...dbNotifs, ...dynamicNotifs];
      if (combined.length > 0) {
        setNotifs(combined);
        const alreadyShown = sessionStorage.getItem('loginNotificationsShown');
        if (!alreadyShown) {
          setNotifOpen(true);
          sessionStorage.setItem('loginNotificationsShown', 'true');
        }
      }
    };

    loadNotifications();
  }, [rolesString, contactId, getEffectiveRoles]);

  const handleCloseNotifications = useCallback(() => {
    setNotifOpen(false);
    const currentRoles = getEffectiveRoles();
    if (hasRole(currentRoles, 'EXTERNAL_IT')) {
      setNotifs([]);
      return;
    }
    if (contactId) {
      api.post(`/notification/${contactId}`)
        .then(() => {
          setNotifs([]);
        })
        .catch(err => console.error("Error marking notifications as read:", err));
    } else {
      setNotifs([]);
    }
  }, [contactId, getEffectiveRoles]);

  const [showFinancialPopup, setShowFinancialPopup] = useState(false);
  const [financialPopupMessage, setFinancialPopupMessage] = useState('');

  useEffect(() => {
    const currentRoles = getEffectiveRoles();
    if (currentRoles.length === 0 || hasRole(currentRoles, 'EXTERNAL_IT')) {
      return;
    }
    const isAccountant = ['ACCOUNTANT', 'CHIEF_ACCOUNTANT', 'SUPERVISOR_ACCOUNTANT'].some(r => hasRole(currentRoles, r));

    if (isAccountant && contactId) {
      const selectedYear = 2025;

      // 1. Sprawdzanie brakujących deklaracji (isDeclaredToDo)
      api.get(`/clients/isAllFinancialStatementsForAccountantDeclaredToDoTrue/${contactId}`)
        .then(res => {
          const isTrue = res.data;
          const isPeriod = true; // tymczasowo true do testów (normalnie: CommonFunctions.isFinancialStatementPeriod())
          if (!isTrue && isPeriod) {
            setFinancialPopupMessage(`Sprawdź zakładkę “Sprawozdania Finansowe ${selectedYear}” i zadeklaruj proszę, które sprawozdania przygotujesz do 31 marca ${parseInt(selectedYear, 10) + 1}.`);
            setShowFinancialPopup(true);
          }
        })
        .catch(err => console.error("Error checking financial statements declaration:", err));
    }
  }, [rolesString, token, contactId, getEffectiveRoles]);

  const isAccountantMenu = hasRole(effectiveRoles, 'ACCOUNTANT') || hasRole(effectiveRoles, 'CHIEF_ACCOUNTANT') || hasRole(effectiveRoles, 'SUPERVISOR_ACCOUNTANT');

  const menuItem = [
    { path: "/ClientsList", name: "Lista klientów", icon: <FaUserTie /> },
    { path: "/MonthlyStatement", name: "Zestawienie księgowej", icon: <FaEdit /> },
    { path: "/GenerateInvoices", name: isViewOnly ? "Status zestawienia" : "Kasa", icon: <SiCashapp /> },
    { path: "/AdditionalCosts", name: isAccountantMenu ? "Prace dodatkowe" : "Zatwierdź koszty dodatkowe", icon: <BiLineChartDown /> },
    { path: "/AccountantsList", name: "Lista księgowych", icon: <FaUserAlt /> },
    { path: "/ApproveStatements", name: "Zatwierdź zestawienia", icon: <FaTasks /> },
    { path: "/SalaryBlocks", name: "Blokady wynagrodzeń", icon: <FaBan /> },
    { path: "/StartAccounting", name: "Zarządzanie klientami", icon: <FaRunning /> },
    { path: "/Overview", name: "Overview", icon: <FaEye /> },
    { path: "/DebtCollection", name: "Windykacja", icon: <FaPeopleArrows /> },
    { path: "/FinancialStatements", name: "Sprawozdania Finansowe", icon: <SiReadthedocs /> },
    { path: "/ClientSurvey", name: "Ankiety klientów", icon: <RiSurveyFill /> },
    { path: "/Imports", name: "Importowanie danych", icon: <FaFileImport /> },
    { path: "/Settings", name: "Opcje i Ustawienia", icon: <FaCog /> },
    { path: "/SignOut", name: "Wyloguj", icon: <FiPower />, action: onSignOut }
  ];

  const handleMenuItemClick = useCallback((item) => {
    if (item.action) {
      item.action();
    }
  }, []);

  const restrictedPaths = {
    ACCOUNTANT: ["/AccountantsList", "/ApproveStatements", "/Overview", "/StartAccounting", "/DebtCollection", "/ClientSurvey", "/Imports"],
    CHIEF_ACCOUNTANT: ["/Overview", "/StartAccounting", "/DebtCollection", "/ClientSurvey", "/Imports"],
    TRADER: ["/GenerateInvoices", "/ApproveStatements", "/MonthlyStatement", "/Overview", "/StartAccounting", "/AdditionalCosts", "/FinancialStatements", "/ClientSurvey", "/Imports", "/SalaryBlocks"],
    ACCOUNT_MANAGER: ["/GenerateInvoices", "/ApproveStatements", "/Overview", "/MonthlyStatement", "/AdditionalCosts", "/ClientSurvey", "/Imports", "/SalaryBlocks"],
    SUPERVISOR: ["/GenerateInvoices", "/Overview", "/DebtCollection", "/AdditionalCosts", "/ClientSurvey", "/Imports"],
    SUPERVISOR_ACCOUNTANT: ["/Overview", "/DebtCollection", "/ClientSurvey", "/Imports"],
    MANAGER: ["/Overview"],
    EXTERNAL_IT: [
      "/MonthlyStatement",
      "/GenerateInvoices",
      "/AdditionalCosts",
      "/ApproveStatements",
      "/SalaryBlocks",
      "/StartAccounting",
      "/Overview",
      "/DebtCollection",
      "/FinancialStatements",
      "/ClientSurvey",
      "/Imports"
    ]
  };

  const rolesRestrictions = {
    ACCOUNTANT: hasRole(effectiveRoles, 'ACCOUNTANT'),
    CHIEF_ACCOUNTANT: hasRole(effectiveRoles, 'CHIEF_ACCOUNTANT'),
    TRADER: hasRole(effectiveRoles, 'TRADER'),
    ACCOUNT_MANAGER: hasRole(effectiveRoles, 'ACCOUNT_MANAGER'),
    SUPERVISOR: hasRole(effectiveRoles, 'SUPERVISOR'),
    SUPERVISOR_ACCOUNTANT: hasRole(effectiveRoles, 'SUPERVISOR_ACCOUNTANT'),
    MANAGER: hasRole(effectiveRoles, 'MANAGER'),
    EXTERNAL_IT: hasRole(effectiveRoles, 'EXTERNAL_IT')
  };

  const isExternalItUser = hasRole(effectiveRoles, 'EXTERNAL_IT');

  const bgColor = darkTheme ? '#262b2e' : '#f4f6f8';

  return (
    <div className={!isVisible ? 'layout-fullscreen' : ''} style={{ backgroundColor: bgColor, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {!isExternalItUser && (
        <Notifications
          open={notifOpen}
          handleClose={handleCloseNotifications}
          initialNotifications={notifs}
        />
      )}

      {!isExternalItUser && (
        <FinancialPopup
          message={financialPopupMessage}
          showCondition={showFinancialPopup}
          storageKey="financialPopupShown"
          buttonLabel={`Sprawozdania Finansowe ${CommonFunctions.getYearForSelect(null, null, false, true)}`}
          onClose={() => setShowFinancialPopup(false)}
        />
      )}

      {/* Modal wymuszający konfigurację 2FA po upływie 30 dni */}
      <EnforcedTwoFactorModal />

      <div className="container" style={{ flex: 1 }}>
        {/* ── Sidebar ── */}
        {isVisible && (
          <div
            style={{ width: isOpen ? '232px' : '56px' }}
            className={`sidebar${isOpen ? '' : ' sidebar--collapsed'}`}
          >
            {/* Top: hamburger + logo text */}
            <div className="top_section">
              <Tooltip title={isOpen ? '' : 'Rozwiń menu'} placement="right" arrow>
                <div className="bars" onClick={toggle}>
                  <FaBars />
                </div>
              </Tooltip>
              {isOpen && <span className="logo">PELIKAN</span>}
            </div>

            {/* Nav items */}
            <div style={{ flex: 1, overflow: 'visible', padding: '6px 0' }}>
              {menuItem.map((item, index) => {
                for (const [key, restricted] of Object.entries(restrictedPaths)) {
                  if (rolesRestrictions[key] && restricted.includes(item.path)) {
                    return null;
                  }
                }

                const isLogout = item.path === '/SignOut';

                // Collapsed: only centred icon, no text
                const collapsedLinkStyle = {
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '9px 0',
                  margin: '2px 6px',
                  borderRadius: '9px',
                  cursor: 'pointer',
                  transition: 'background 150ms ease, color 150ms ease',
                  color: '#CBD5E1',
                };

                const linkContent = isOpen ? (
                  <>
                    <div className="icon">{item.icon}</div>
                    <div className="link_text">{item.name}</div>
                  </>
                ) : (
                  <div style={{ fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {item.icon}
                  </div>
                );

                const wrappedLink = item.action ? (
                  <div
                    key={index}
                    className={isOpen ? `link${isLogout ? ' link--logout' : ''}` : undefined}
                    style={!isOpen ? collapsedLinkStyle : undefined}
                    onClick={() => handleMenuItemClick(item)}
                  >
                    {linkContent}
                  </div>
                ) : (
                  <NavLink
                    key={index}
                    to={item.path}
                    className={isOpen ? (({ isActive }) => isActive ? 'link active' : 'link') : undefined}
                    style={!isOpen ? ({ isActive }) => ({
                      ...collapsedLinkStyle,
                      ...(isActive ? {
                        background: 'rgba(255, 255, 255, 0.08)',
                        color: '#FFFFFF',
                        boxShadow: 'inset 3px 0 0 #3B82F6',
                      } : {}),
                    }) : undefined}
                  >
                    {linkContent}
                  </NavLink>
                );

                return (
                  <React.Fragment key={index}>
                    {isLogout && <div className="sidebar-divider" />}
                    {!isOpen ? (
                      <Tooltip title={item.name} placement="right" arrow>
                        {wrappedLink}
                      </Tooltip>
                    ) : wrappedLink}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}


        {/* ── Main content ── */}
        <main>
          {/* Header bar */}
          {isVisible ? (
            <div className={`app-header${darkTheme ? ' app-header--dark' : ''}`}>
              {/* Logo */}
              <div className="app-header__logo">
                <img alt="logo" src={darkTheme ? logo1 : logo} />
              </div>

              {/* Page title (desktop only) */}
              {isDesktop && (
                <div className="app-header__title">
                  {pageTitle && <span className="sidebar-title">{pageTitle}</span>}
                </div>
              )}

              {/* Actions: user avatar + buttons */}
              <div className="app-header__actions">
                {/* User avatar + name */}
                <div className="user-info" style={{ color: darkTheme ? '#94A3B8' : '#64748B' }}>
                  <div className="user-avatar" title={userName}>{initials}</div>
                  {isDesktop && (
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{userName}</span>
                  )}
                </div>

                {/* Fullscreen toggle */}
                <Tooltip title="Pełny ekran" arrow>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={toggleVisibility}
                    sx={{ minWidth: 36, px: 0.5 }}
                  >
                    <FullscreenIcon fontSize="small" />
                  </Button>
                </Tooltip>

                {/* Theme toggle */}
                <Tooltip title={darkTheme ? 'Tryb jasny' : 'Tryb ciemny'} arrow>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={toggleTheme}
                    sx={{ minWidth: 36, px: 0.5 }}
                  >
                    {darkTheme
                      ? <LightModeIcon fontSize="small" />
                      : <DarkModeIcon fontSize="small" />
                    }
                  </Button>
                </Tooltip>
              </div>
            </div>
          ) : (
            /* Fullscreen mode floating controls */
            <div style={{
              position: 'fixed',
              top: 10,
              right: 20,
              zIndex: 9999,
              display: 'flex',
              gap: 8,
              background: darkTheme ? 'rgba(22, 27, 34, 0.8)' : 'rgba(255, 255, 255, 0.8)',
              borderRadius: 8,
              padding: 4,
              boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
            }}>
              <Tooltip title="Widok standardowy" arrow>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={toggleVisibility}
                  sx={{ minWidth: 36, px: 0.5 }}
                >
                  <FullscreenExitIcon fontSize="small" />
                </Button>
              </Tooltip>
              <Tooltip title={darkTheme ? 'Tryb jasny' : 'Tryb ciemny'} arrow>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={toggleTheme}
                  sx={{ minWidth: 36, px: 0.5 }}
                >
                  {darkTheme
                    ? <LightModeIcon fontSize="small" />
                    : <DarkModeIcon fontSize="small" />
                  }
                </Button>
              </Tooltip>
            </div>
          )}

          {/* Page content */}
          <div className="main-content">
            <TwoFactorBanner />
            <Outlet />
            <Copyright sx={{ mt: 8, mb: 4 }} />
          </div>
        </main>
      </div>
    </div>
  );
};

export default React.memo(Sidebar);
